import { z } from 'zod';
import type { SearchType } from '@/types';
import { ONE_TIME_PRICING } from '@/config/pricing';

/**
 * Address entry schema
 */
const addressEntrySchema = z.object({
  address: z.string().max(100, 'Address must be less than 100 characters').optional().or(z.literal('')),
  city: z.string().max(50, 'City must be less than 50 characters').optional().or(z.literal('')),
  state: z.string().length(2, 'State must be 2 letters (e.g., CA, NY)').optional().or(z.literal('')),
  zip: z.string().regex(/^\d{5}(-\d{4})?$/, 'ZIP must be 5 digits or ZIP+4 format').optional().or(z.literal('')),
});

/**
 * Validation schema for single person search
 * Supports single entry (enrich operations) and multiple entries (validate/scoring operations)
 */
export const singleSearchSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'First name can only contain letters, spaces, hyphens, and apostrophes'),

  lastName: z
    .string()
    .min(1, 'Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Last name can only contain letters, spaces, hyphens, and apostrophes'),

  amountToCollect: z
    .number({ message: 'Amount must be a number' })
    .min(0.01, 'Amount must be at least $0.01')
    .max(1000000, 'Amount cannot exceed $1,000,000')
    .refine((val) => Number.isFinite(val), 'Amount must be a valid number')
    .optional(),

  searchType: z.enum(['validate', 'enrich', 'enrich_validate', 'scoring', 'scoring_enrich'], {
    message: 'Invalid search type',
  }),

  // Single entry fields (backward compatible for enrich operations)
  address: z.string().max(100, 'Address must be less than 100 characters').optional().or(z.literal('')),
  city: z.string().max(50, 'City must be less than 50 characters').optional().or(z.literal('')),
  state: z.string().length(2, 'State must be 2 letters (e.g., CA, NY)').optional().or(z.literal('')),
  zip: z.string().regex(/^\d{5}(-\d{4})?$/, 'ZIP must be 5 digits or ZIP+4 format').optional().or(z.literal('')),
  phone: z.string().regex(/^\d{10}$/, 'Phone must be 10 digits').optional().or(z.literal('')),
  email: z.string().email('Invalid email address').optional().or(z.literal('')),

  // Multiple entry fields (for validate/scoring operations)
  addresses: z.array(addressEntrySchema).max(5, 'Maximum 5 addresses allowed').optional(),
  phones: z.array(z.string().regex(/^\d{10}$/, 'Phone must be 10 digits').or(z.literal(''))).max(5, 'Maximum 5 phone numbers allowed').optional(),
  emails: z.array(z.string().email('Invalid email address').or(z.literal(''))).max(5, 'Maximum 5 email addresses allowed').optional(),
}).refine(
  (data) => {
    // At least one contact method must be provided
    const hasSinglePhone = data.phone && data.phone.length > 0;
    const hasSingleEmail = data.email && data.email.length > 0;
    const hasSingleAddress = data.address && data.address.length > 0;

    const hasMultiplePhones = data.phones && data.phones.some(p => p && p.length > 0);
    const hasMultipleEmails = data.emails && data.emails.some(e => e && e.length > 0);
    const hasMultipleAddresses = data.addresses && data.addresses.some(a => a.address && a.address.length > 0);

    return hasSinglePhone || hasSingleEmail || hasSingleAddress || hasMultiplePhones || hasMultipleEmails || hasMultipleAddresses;
  },
  {
    message: 'Please provide at least one contact method: phone number, email address, or street address',
    path: ['phone'], // Show error on phone field
  }
).refine(
  (data) => {
    // Amount to collect is required for scoring operations only
    if (data.searchType === 'scoring' || data.searchType === 'scoring_enrich') {
      return data.amountToCollect !== undefined && data.amountToCollect > 0;
    }
    return true;
  },
  {
    message: 'Amount to collect is required for scoring operations',
    path: ['amountToCollect'],
  }
);

export type SingleSearchFormData = z.infer<typeof singleSearchSchema>;

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date for display
 */
export function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  return phone;
}

/**
 * Get search type display name
 */
export function getSearchTypeDisplayName(searchType: SearchType): string {
  switch (searchType) {
    case 'validate':
      return 'Identity Validation';
    case 'enrich':
      return 'Contact Data Enrichment';
    case 'enrich_validate':
      return 'Enrich + Validate';
    case 'scoring':
      return 'CollectABILITY Scoring';
    case 'scoring_enrich':
      return 'Scoring + Enrich';
    default:
      return searchType;
  }
}

/**
 * Get search type price (one-time pricing for non-authenticated users)
 * @deprecated Use getOneTimeSearchCost() or getWalletSearchCost() from @/config/pricing instead
 * This function returns one-time pricing for backward compatibility
 */
export function getSearchTypePrice(searchType: SearchType): number {
  return ONE_TIME_PRICING[searchType] || 0;
}

/**
 * Validate and sanitize search input
 */
export function validateSearchInput(data: unknown): SingleSearchFormData {
  return singleSearchSchema.parse(data);
}
